import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Paper,
  Alert,
  Autocomplete,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
// import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import useProjectTemplateStore from '../../../stores/projectTemplateStore';
// import { PageContainer } from '../../../design-system';

const PROJECT_TYPES = [
  'general', 
  'website_development', 
  'content_creation', 
  'social_media_management', 
  'branding', 
  'rebranding', 
  'marketing_campaign', 
  'advertising_campaign', 
  'video_production', 
  'event_management', 
  'other'
];

const PROJECT_PRIORITIES = ['low', 'medium', 'high', 'urgent'];
const PROJECT_CATEGORIES = ['general', 'marketing', 'development', 'content', 'custom'];

const validationSchema = Yup.object({
  name: Yup.string()
    .required('Template name is required')
    .min(3, 'Name must be at least 3 characters')
    .max(100, 'Name cannot exceed 100 characters'),
  description: Yup.string().max(500, 'Description cannot exceed 500 characters'),
  category: Yup.string().required('Category is required'),
  sections: Yup.array()
    .min(1, 'At least one section is required')
    .of(
      Yup.object({
        name: Yup.string().required('Section name is required'),
        orderIndex: Yup.number().required()
      })
    ),
  projectConfig: Yup.object({
    type: Yup.string().required('Project type is required'),
    priority: Yup.string().required('Priority is required'),
    defaultDuration: Yup.number()
      .min(1, 'Duration must be at least 1 day')
      .max(365, 'Duration cannot exceed 365 days'),
    tags: Yup.array().of(Yup.string())
  })
});

const ProjectTemplateBuilder = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  
  const {
    currentTemplate,
    isLoading,
    error,
    fetchTemplateById,
    createTemplate,
    updateTemplate,
    clearError
  } = useProjectTemplateStore();

  const [sectionDialog, setSectionDialog] = useState({ open: false, section: null, index: -1 });
  const [tagInput, setTagInput] = useState('');
  const isEditing = Boolean(id);

  // Load template if editing
  useEffect(() => {
    if (isEditing && id) {
      fetchTemplateById(id);
    }
  }, [isEditing, id, fetchTemplateById]);

  // Initialize form
  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
      category: 'custom',
      sections: [{ name: 'Draft', orderIndex: 0 }],
      projectConfig: {
        type: 'general',
        priority: 'medium',
        defaultDuration: 30,
        tags: []
      }
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      
      try {
        if (isEditing) {
          const result = await updateTemplate(id, values);
          enqueueSnackbar('Template updated successfully', { variant: 'success' });
        } else {
          const result = await createTemplate(values);
          enqueueSnackbar('Template created successfully', { variant: 'success' });
        }
        navigate('/project-templates');
      } catch (error) {
        console.error('Form submission error:', error);
        enqueueSnackbar(error.message || 'Failed to save template', { variant: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  // Update form when template loads
  useEffect(() => {
    if (currentTemplate && isEditing) {
      formik.setValues({
        name: currentTemplate.name || '',
        description: currentTemplate.description || '',
        category: currentTemplate.category || 'custom',
        sections: currentTemplate.sections || [],
        projectConfig: {
          type: currentTemplate.projectConfig?.type || 'general',
          priority: currentTemplate.projectConfig?.priority || 'medium',
          defaultDuration: currentTemplate.projectConfig?.defaultDuration || 30,
          tags: currentTemplate.projectConfig?.tags || []
        }
      });
    }
  }, [currentTemplate, isEditing]);

  // Handle section operations
  const handleAddSection = () => {
    setSectionDialog({ 
      open: true, 
      section: { name: '', orderIndex: formik.values.sections.length }, 
      index: -1 
    });
  };

  const handleEditSection = (section, index) => {
    setSectionDialog({ open: true, section: { ...section }, index });
  };


  const handleDeleteSection = (index) => {
    const sections = formik.values.sections.filter((_, i) => i !== index);
    // Update order indexes
    const updatedSections = sections.map((section, idx) => ({
      ...section,
      orderIndex: idx
    }));
    formik.setFieldValue('sections', updatedSections);
  };

  // Handle section reordering
  const handleMoveSection = (index, direction) => {
    const sections = [...formik.values.sections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= sections.length) return;
    
    // Swap sections
    [sections[index], sections[newIndex]] = [sections[newIndex], sections[index]];
    
    // Update order indexes
    const updatedSections = sections.map((section, idx) => ({
      ...section,
      orderIndex: idx
    }));

    formik.setFieldValue('sections', updatedSections);
  };

  // Handle tags
  const handleAddTag = () => {
    if (tagInput.trim() && !formik.values.projectConfig.tags.includes(tagInput.trim())) {
      formik.setFieldValue('projectConfig.tags', [
        ...formik.values.projectConfig.tags,
        tagInput.trim()
      ]);
      setTagInput('');
    }
  };

  const handleDeleteTag = (tagToDelete) => {
    formik.setFieldValue(
      'projectConfig.tags',
      formik.values.projectConfig.tags.filter(tag => tag !== tagToDelete)
    );
  };

  // Separate component to prevent re-renders
  const SectionDialog = React.memo(() => {
    const [localSectionName, setLocalSectionName] = useState('');
    
    // Update local state when dialog opens
    React.useEffect(() => {
      if (sectionDialog.open) {
        setLocalSectionName(sectionDialog.section?.name || '');
      }
    }, [sectionDialog.open, sectionDialog.section?.name]);
    
    const handleClose = () => {
      setSectionDialog({ open: false, section: null, index: -1 });
      setLocalSectionName('');
    };
    
    const handleSave = () => {
      const sections = [...formik.values.sections];
      const sectionData = {
        name: localSectionName.trim(),
        orderIndex: sectionDialog.index >= 0 ? sectionDialog.section.orderIndex : formik.values.sections.length
      };
      
      if (sectionDialog.index >= 0) {
        // Edit existing section
        sections[sectionDialog.index] = sectionData;
      } else {
        // Add new section
        sections.push(sectionData);
      }
      
      formik.setFieldValue('sections', sections);
      handleClose();
    };
    
    return (
      <Dialog
        open={sectionDialog.open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {sectionDialog.index >= 0 ? 'Edit Section' : 'Add Section'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Section Name"
            value={localSectionName}
            onChange={(e) => setLocalSectionName(e.target.value)}
            margin="normal"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!localSectionName.trim()}
          >
            {sectionDialog.index >= 0 ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  });

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {isEditing ? 'Edit Template' : 'Create Template'}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {isEditing ? 'Modify your project template' : 'Create a new reusable project template'}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Template Name"
                      name="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.name && Boolean(formik.errors.name)}
                      helperText={formik.touched.name && formik.errors.name}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl 
                      fullWidth
                      error={formik.touched.category && Boolean(formik.errors.category)}
                    >
                      <InputLabel>Category</InputLabel>
                      <Select
                        name="category"
                        value={formik.values.category}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Category"
                        required
                      >
                        {PROJECT_CATEGORIES.map((category) => (
                          <MenuItem key={category} value={category}>
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      name="description"
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.description && Boolean(formik.errors.description)}
                      helperText={formik.touched.description && formik.errors.description}
                      multiline
                      rows={3}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Project Configuration */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Project Defaults
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Project Type</InputLabel>
                      <Select
                        name="projectConfig.type"
                        value={formik.values.projectConfig.type}
                        onChange={formik.handleChange}
                        label="Project Type"
                      >
                        {PROJECT_TYPES.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Priority</InputLabel>
                      <Select
                        name="projectConfig.priority"
                        value={formik.values.projectConfig.priority}
                        onChange={formik.handleChange}
                        label="Priority"
                      >
                        {PROJECT_PRIORITIES.map((priority) => (
                          <MenuItem key={priority} value={priority}>
                            {priority.charAt(0).toUpperCase() + priority.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Default Duration (days)"
                      name="projectConfig.defaultDuration"
                      type="number"
                      value={formik.values.projectConfig.defaultDuration}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.projectConfig?.defaultDuration && Boolean(formik.errors.projectConfig?.defaultDuration)}
                      helperText={formik.touched.projectConfig?.defaultDuration && formik.errors.projectConfig?.defaultDuration}
                      InputProps={{ inputProps: { min: 1, max: 365 } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Default Tags
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                      {formik.values.projectConfig.tags.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          onDelete={() => handleDeleteTag(tag)}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        size="small"
                        placeholder="Add tag"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddTag();
                          }
                        }}
                        sx={{ flexGrow: 1 }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleAddTag}
                        disabled={!tagInput.trim()}
                      >
                        Add
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Sections */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Sections ({formik.values.sections.length})
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddSection}
                  >
                    Add Section
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />

                {formik.touched.sections && formik.errors.sections && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {formik.errors.sections}
                  </Alert>
                )}

                <List dense>
                  {formik.values.sections.map((section, index) => {
                    const isDraftSection = section.name === 'Draft' && index === 0;
                    
                    return (
                      <ListItem
                        key={`section-${index}`}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1,
                          backgroundColor: 'background.paper'
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', mr: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveSection(index, 'up')}
                            disabled={index === 0 || isDraftSection}
                          >
                            <ArrowUpIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveSection(index, 'down')}
                            disabled={index === formik.values.sections.length - 1 || isDraftSection}
                          >
                            <ArrowDownIcon />
                          </IconButton>
                        </Box>
                        <ListItemText
                          primary={section.name}
                          secondary={isDraftSection ? 'Default section' : `Order: ${index + 1}`}
                        />
                        <ListItemSecondaryAction>
                          {!isDraftSection && (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleEditSection(section, index)}
                                sx={{ mr: 1 }}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteSection(index)}
                                disabled={formik.values.sections.length <= 1}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </>
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>

                {formik.values.sections.length === 0 && (
                  <Paper sx={{ p: 3, textAlign: 'center', border: '2px dashed', borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary">
                      No sections added yet. Click "Add Section" to get started.
                    </Typography>
                  </Paper>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Form Actions */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/project-templates')}
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading || !formik.isValid}
                startIcon={<SaveIcon />}
                onClick={async (e) => {
                  // Validate the form first
                  const errors = await formik.validateForm();
                  
                  // Don't submit if there are validation errors
                  if (Object.keys(errors).length === 0) {
                    formik.handleSubmit(e);
                  }
                }}
              >
                {isEditing ? 'Update Template' : 'Create Template'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      <SectionDialog />
    </Box>
  );
};

export default ProjectTemplateBuilder;